/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import * as Sentry from "@sentry/vue";
import 'resize-observer-polyfill';  // must come BEFORE Chart.js
import { createApp, h, Fragment } from 'vue';
import { createInertiaApp, Link, Head } from '@inertiajs/vue3';
import { router } from '@inertiajs/vue3';

// Prevent progress bar from staying visible forever if a visit never completes
let progressTimeout = null;
router.on('start', () => {
    progressTimeout = setTimeout(() => {
        const bar = document.querySelector('#nprogress, .nprogress');
        if (bar) {
            bar.style.opacity = '0';
            bar.style.transition = 'opacity 0.2s';
        }
        progressTimeout = null;
    }, 15000);
});
router.on('finish', () => {
    if (progressTimeout) {
        clearTimeout(progressTimeout);
        progressTimeout = null;
    }
});
import { createPinia } from 'pinia';
import filtersMixin from './mixins/filters';
import { useUserStore } from './stores/user';
import { useClientStore } from './stores/client';
import statsigService from './services/statsig';
import Notifications from './components/components/Notifications.vue';

require('./bootstrap');

/**
 * Automatically register Vue components from the components and Layouts directories.
 * We exclude the Pages directory to prevent name collisions and overhead.
 */
const componentFiles = require.context('./components', true, /\.vue$/i);
const layoutFiles = require.context('./Layouts', true, /\.vue$/i);
const pinia = createPinia();

numeral.register('locale', 'na', {
    delimiters: {
        thousands: ',',
        decimal: '.'
    },
    abbreviations: {
        thousand: 'K',
        million: 'M',
        billion: 'B',
        trillion: 'T'
    },
    ordinal: function (number) {
        var b = number % 10;
        return (~~ (number % 100 / 10) === 1) ? 'th' :
            (b === 1) ? 'st' :
            (b === 2) ? 'nd' :
            (b === 3) ? 'rd' : 'th';
    },
    currency: {
        symbol: '$'
    }
});
// switch between locales
numeral.locale('na');

function setupApp(app) {
    app.use(pinia);

    // Register Inertia components globally
    app.component('Link', Link);
    app.component('Head', Head);

    // Add the filters mixin to the app
    app.mixin(filtersMixin);

    // Register components
    componentFiles.keys().forEach(key => {
        const component = componentFiles(key).default;
        const name = key.split('/').pop().split('.')[0];
        app.component(name, component);
    });

    // Register Layouts
    layoutFiles.keys().forEach(key => {
        const component = layoutFiles(key).default;
        const name = key.split('/').pop().split('.')[0];
        app.component(name, component);
    });

    Sentry.init({
        app,
        environment: (window.app_env || 'production'),
        dsn: "https://4ebef61232e9094cf532e1ed99894822@o4508020195131392.ingest.us.sentry.io/4508020197818368",
        integrations: [
          Sentry.replayIntegration(),
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event, hint) {
          // Don't send errors from local development environment
          const url = event.request?.url || window.location.href;
          if (url && url.startsWith('https://local.avidai.com/')) {
            return null; // Don't send the event
          }
          return event;
        },
      });
      
    // Initialize stores with window data
    if (window.user) {
      const userStore = useUserStore();
      userStore.initialize(window.user);
      statsigService.initialize(window.user);
    } else {
      statsigService.initialize();
    }

    if (window.client) {
      const clientStore = useClientStore();
      clientStore.initialize(window.client, window.userAccessPermissions || {}, window.clientTasks || []);
      // Expose globally for non-Vue scripts
      window.clientStore = clientStore;
    }
}

const el = document.getElementById('app');

if (el && el.dataset.page) {
    // Helper function to update window globals from Inertia page props
    function updateWindowGlobals(pageProps) {
        if (pageProps?.auth?.user) {
            window.user = pageProps.auth.user;
        }
        if (pageProps?.client) {
            window.client = pageProps.client;
        }
        if (pageProps?.userAccessPermissions) {
            window.userAccessPermissions = pageProps.userAccessPermissions;
        }
    }

    createInertiaApp({
        title: title => title ? `${title}` : 'Avid',
        resolve: name => {
            return require(`./Pages/${name}`).default;
        },
        setup({ el, App, props, plugin }) {
            // Set window globals from initial Inertia page props
            if (props.initialPage?.props) {
                updateWindowGlobals(props.initialPage.props);
            }
            // Mount Notifications outside the Inertia page tree so it persists across navigation
            const app = createApp({
                render: () => h(Fragment, null, [h(App, props), h(Notifications)]),
            });
            app.use(plugin);
            setupApp(app);
            app.mount(el);
        },
        progress: {
            color: '#4B5563',
            delay: 300,
        },
    });
} else {
    const app = createApp({});
    setupApp(app);
    app.mount('#app');
}

const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl))
